import _head from "lodash/head";
import React, { useEffect } from "react";
import Drawer from "web/react/components/drawer/drawer";
import FeedsFilters from "web/react/components/feeds-filters/feeds-filters";
import {
    PaginatedItem,
    usePageManager,
} from "web/react/components/feeds-filters/feeds-filters-pagination/pagination-state";
import { useFilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { useOverlayHandler } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import { useDesktopBreakpoint } from "web/react/hooks/use-desktop-breakpoint/use-desktop-breakpoint";
import withReduxProvider from "web/react/redux-provider";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import {
    CategoryFilterOption,
    isFilterSection,
    UniversalFiltersData,
} from "web/types/universal-filters";

interface FilterContainerProps {
    filters: UniversalFiltersData;
}
export function FilterContainer({ filters }: FilterContainerProps): JSX.Element {
    let pageManager = usePageManager(gettext("general.filters"));

    const {
        popActivePage,
        pushActivePage,
        paginationState,
        popPage,
        resetPagination,
        title: overlayTitle,
    } = pageManager;

    let filterManager = useFilterManager(filters);
    const {
        applyState,
        canApply,
        cancelState,
        displayedFilters,
        updateFromServer,
        ...otherFilterProps
    } = filterManager;

    const { applyAndClose, cancelAndClose, overlayIsOpen } = useOverlayHandler(
        applyState,
        cancelState
    );

    const isDesktop = useDesktopBreakpoint();
    const treeStructure = environment.getFeature("uj_tree_filter_all_screens");

    useEffect(() => {
        let categorySection = _head(
            displayedFilters.filter_section_list.filter(
                (section) => section.id === "section-category"
            )
        );
        if (!categorySection) {
            return;
        }

        if (new URLSearchParams(displayedFilters.query_string).has("product_type")) {
            return;
        }

        if (!(isDesktop || treeStructure)) {
            return;
        }

        // open the category filter section on page load if no product type is selected
        pushActivePage([categorySection]);
        // We only ever want to run this hook once, the first time we have a filter_section_list
        // (some pages initialise with out a filter_section_list and then load it (e.g. /search/)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedFilters.filter_section_list.length == 0]);

    useEffect(() => {
        if (treeStructure && !isDesktop && overlayIsOpen) {
            updateFromServer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationState]);

    useEffect(() => {
        // Reset the pagination whenever the overlay opens or closes
        resetPagination();
    }, [overlayIsOpen, resetPagination]);

    function onBack(): void {
        if (treeStructure || !paginationState.hasActivePage()) {
            applyAndClose();
            return;
        }
        popActivePage();
        updateFromServer();
    }

    let pushMoreThenOneItem = treeStructure || isDesktop;

    function handleItemClick(item: PaginatedItem): void {
        if (isFilterSection(item) && pushMoreThenOneItem && item.id == "section-category") {
            let options = item.option_groups[0].options as CategoryFilterOption[];
            let child = options.find((child) => child.checked);

            if (child) {
                pushActivePage([item, child]);
                return;
            }
        }
        pushActivePage([item]);
    }

    return (
        <Drawer
            title={treeStructure ? gettext("general.filters") : overlayTitle}
            id={"feeds-filter"}
            open={overlayIsOpen}
            onCancel={cancelAndClose}
            onBack={onBack}
            onOverlay={applyAndClose}
        >
            <FeedsFilters
                applyState={applyAndClose}
                canApply={canApply}
                displayedFilters={displayedFilters}
                isOpen={overlayIsOpen}
                onClose={popPage}
                onPageChange={handleItemClick}
                paginationState={paginationState}
                {...otherFilterProps}
            />
        </Drawer>
    );
}

export default withReduxProvider(FilterContainer);
